/**
 * Servicio de notificaciones por email para cotizaciones
 * 
 * Este servicio integra las funcionalidades de SendGrid con el flujo de cotizaciones
 * para enviar notificaciones automáticas en diferentes eventos.
 */

import Parse from '../../config/parse';
import { 
  sendQuotationConfirmation, 
  sendStatusUpdateNotification,
  sendWelcomeEmail,
  sendTemplateEmail,
  sendEmail,
  TEMPLATE_IDS
} from './sendgridService';

/**
 * Obtiene la URL base de la aplicación para generar enlaces
 * @returns {string} URL base de la aplicación
 */
const getAppUrl = () => {
  // Primero intentamos obtener la URL desde las variables de entorno
  const envUrl = import.meta.env.VITE_APP_URL;
  
  if (envUrl) {
    console.log('Usando URL de la aplicación desde variables de entorno:', envUrl);
    return envUrl;
  }
  
  // Si no está disponible, determinamos si estamos en desarrollo o producción
  const isProduction = import.meta.env.PROD;
  const defaultUrl = isProduction 
    ? 'https://vvhealth.vercel.app' 
    : 'http://localhost:5173';
  
  console.log(`URL de la aplicación no configurada en variables de entorno. Usando valor por defecto para ${isProduction ? 'producción' : 'desarrollo'}: ${defaultUrl}`);
  return defaultUrl;
};

/**
 * Envía una notificación de confirmación cuando se crea una nueva cotización
 * @param {Object} quotation - Objeto de cotización de Parse
 * @returns {Promise} - Promesa que se resuelve cuando el email es enviado
 */
export const notifyNewQuotation = async (quotation) => {
  try {
    // Usar el email real del cliente
    const userEmail = quotation.get('userEmail');

    // Obtener datos para la plantilla
    let userName = quotation.get('userName') || '';
    // Ensure userName is not empty and properly formatted
    if (!userName || userName.trim() === '') {
      userName = 'Cliente';
    } else {
      // Capitalize first letter of each word
      userName = userName.split(' ').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      ).join(' ');
    }
    
    console.log('Sending email with client name:', userName);
    const productType = quotation.get('productType') || 'Producto';
    const quotationId = quotation.id;
    const userPhone = quotation.get('userPhone') || 'No proporcionado';
    const userCompany = quotation.get('userCompany') || 'No proporcionada';
    const message = quotation.get('message') || '';
    const additionalNotes = quotation.get('additionalNotes') || '';
    
    // Obtener el magic link para continuar con la creación de cuenta
    const magicLink = quotation.get('magicLink');
    const appUrl = getAppUrl();
    
    // Construir URLs absolutas para los enlaces
    const trackingUrl = `${appUrl}/quotation/track/${magicLink}`;
    const detailUrl = `${appUrl}/quotation/detail/${magicLink}`;
    const authUrl = `${appUrl}/quotation/auth/${magicLink}`; // URL para crear cuenta
    const adminUrl = `${appUrl}/admin/quotations/${quotationId}`; // URL para administración
    
    console.log('Generando URLs para nueva cotización:', {
      magicLink,
      appUrl,
      trackingUrl,
      detailUrl,
      authUrl,
      adminUrl
    });

    // Cargar el servicio de email
    const { sendTemplateEmail, TEMPLATE_IDS } = await import('./sendgridService');
    
    // 1. Enviar email de confirmación al CLIENTE con magic link
    const clientResult = await sendTemplateEmail({
      to: userEmail,
      templateId: TEMPLATE_IDS.WELCOME_QUOTATION,
      dynamicTemplateData: {
        nombre_cliente: userName,
        email_cliente: userEmail,
        tipo_producto: productType,
        id_cotizacion: quotationId,
        enlace_seguimiento: trackingUrl,
        enlace_detalle: detailUrl,
        enlace_auth: authUrl
      }
    });

    if (clientResult) {
      console.log(`Email de nueva cotización enviado exitosamente a ${userEmail} usando plantilla ${TEMPLATE_IDS.WELCOME_QUOTATION}`);
    } else {
      console.warn(`No se pudo enviar el email de nueva cotización a ${userEmail}`);
      // Intentar con el método de respaldo si falla la plantilla
      await sendQuotationConfirmation(quotation, userEmail);
    }
    
    // 2. Enviar notificación a VV HEALTH (equipo de ventas)
    // Asegurarse de que tengamos ítems válidos y únicos
    const items = quotation.get('items') || [];
    console.log('Items en notificación de cotización:', items);
    
    // Crear versión de texto para email plain text
    const itemsText = items.map(item => {
      const name = item.name || 'No especificado';
      const quantity = item.bulkQuantity || 0;
      const code = item.code || 'Sin SKU';
      const countPerBottle = (item.countPerBottle && item.countPerBottle[0]) ? item.countPerBottle[0] : 'N/A';
      return `- ${name} (${code}): ${quantity} unidades / ${countPerBottle}`;
    }).join('\n');
    
    // Crear versión HTML para email con formato
    const productList = items.map(item => {
      const name = item.name || 'No especificado';
      const code = item.code || 'Sin SKU';
      const countPerBottle = (item.countPerBottle && item.countPerBottle[0]) ? item.countPerBottle[0] : 'N/A';
      const quantity = item.bulkQuantity || 0;
      
      return `<tr>
        <td>${name}</td>
        <td>${code}</td>
        <td>${countPerBottle}</td>
        <td align="right">${quantity}</td>
      </tr>`;
    }).join('');
    
    // HTML personalizado para el administrador
    const adminHtmlContent = `
    <!DOCTYPE html>
    <html lang="es">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Nueva Solicitud de Cotización - VV Health</title>
      <style>
        body {
          font-family: Arial, sans-serif;
          line-height: 1.6;
          color: #333;
          margin: 0;
          padding: 0;
        }
        .container {
          max-width: 600px;
          margin: 0 auto;
          background-color: #ffffff;
          border: 1px solid #e0e0e0;
          border-radius: 5px;
        }
        .header {
          background-color: #3c8653;
          color: white;
          padding: 20px;
          text-align: center;
        }
        .content {
          padding: 30px;
        }
        .notification-banner {
          background-color: #f8f1d5;
          border-left: 4px solid #e6bb46;
          padding: 12px 20px;
          margin-bottom: 20px;
          font-weight: bold;
        }
        .client-info {
          background-color: #f5f5f5;
          border-radius: 5px;
          padding: 15px;
          margin-bottom: 20px;
        }
        .action-button {
          display: inline-block;
          background-color: #3c8653;
          color: white;
          text-decoration: none;
          padding: 12px 20px;
          border-radius: 4px;
          margin-top: 10px;
        }
        .footer {
          background-color: #f5f5f5;
          padding: 20px;
          text-align: center;
          font-size: 12px;
          color: #666;
        }
        table {
          width: 100%;
          border-collapse: collapse;
        }
        table, th, td {
          border: 1px solid #ddd;
        }
        th, td {
          padding: 8px;
        }
        th {
          background-color: #f2f2f2;
          text-align: left;
        }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="header">
          <h2>NOTIFICACIÓN DE NUEVA COTIZACIÓN</h2>
        </div>
        <div class="content">
          <div class="notification-banner">
            Se ha recibido una nueva solicitud de cotización que requiere revisión inmediata.
          </div>
          
          <p>Estimado Equipo de VV Health,</p>
          
          <p>El cliente <strong>${userName}</strong> ha enviado una nueva solicitud de cotización a través del sitio web. A continuación, los detalles:</p>
          
          <div class="client-info">
            <h3>Información del Cliente</h3>
            <p><strong>Nombre:</strong> ${userName}</p>
            <p><strong>Email:</strong> ${userEmail}</p>
            <p><strong>Teléfono:</strong> ${userPhone}</p>
            <p><strong>Empresa:</strong> ${userCompany}</p>
            <p><strong>ID de Cotización:</strong> #${quotationId}</p>
            <p><strong>Fecha de Solicitud:</strong> ${new Date().toLocaleString('es-PR')}</p>
          </div>
          
          <h3>Productos Solicitados (${items.length})</h3>
          <table>
            <thead>
              <tr>
                <th>Producto</th>
                <th>SKU</th>
                <th>Pastillas/Frasco</th>
                <th align="right">Cantidad</th>
              </tr>
            </thead>
            <tbody>
              ${productList}
            </tbody>
          </table>
          
          <div class="client-info" style="margin-top: 20px;">
            <h3>Mensaje del Cliente</h3>
            <p>${message || 'No se proporcionó mensaje.'}</p>
            
            <h3>Notas Adicionales</h3>
            <p>${additionalNotes || 'No se proporcionaron notas adicionales.'}</p>
          </div>
          
          <p>Por favor, proceda a revisar esta solicitud y preparar una cotización para el cliente lo antes posible.</p>
          
          <a href="${adminUrl}" class="action-button">Revisar en el Panel de Administración</a>
          
          <p style="margin-top: 30px;">Gracias por su atención.</p>
          <p>Notificación Automática - Sistema de Cotizaciones VV Health</p>
        </div>
        
        <div class="footer">
          <p>&copy; ${new Date().getFullYear()} VV Health. Todos los derechos reservados.</p>
          <p>Esta es una notificación automática. Por favor no responda a este correo.</p>
        </div>
      </div>
    </body>
    </html>
    `;
    
    // Enviamos como HTML regular en lugar de usar template ID
    const vvHealthResult = await sendEmail({
      to: ['info@vvhealthpr.com'], // Email oficial para notificaciones
      subject: `[NUEVA COTIZACIÓN] ${userName} - #${quotationId}`,
      html: adminHtmlContent,
      text: `NUEVA SOLICITUD DE COTIZACIÓN\n\nCliente: ${userName}\nEmail: ${userEmail}\nTeléfono: ${userPhone}\nEmpresa: ${userCompany}\n\nProductos (${items.length}):\n${itemsText}\n\nMensaje: ${message || 'No proporcionado'}\n\nNotas: ${additionalNotes || 'No proporcionadas'}\n\nRevise esta solicitud en: ${adminUrl}\n\nGracias, Sistema de Cotizaciones VV Health`
    });
    
    if (vvHealthResult) {
      console.log(`Notificación de nueva cotización enviada exitosamente al equipo de ventas`);
    } else {
      console.warn(`No se pudo enviar la notificación al equipo de ventas`);
    }
    
    // Devolver éxito si al menos uno de los emails se envió correctamente
    return clientResult || vvHealthResult;
  } catch (error) {
    console.error('Error al enviar notificación de nueva cotización:', error);
    // No lanzamos el error para evitar interrumpir el flujo principal
    return null;
  }
};

/**
 * Envía una notificación cuando cambia el estado de una cotización
 * @param {Object} quotation - Objeto de cotización de Parse
 * @param {string} oldStatus - Estado anterior de la cotización
 * @param {string} newStatus - Nuevo estado de la cotización
 * @returns {Promise} - Promesa que se resuelve cuando el email es enviado
 */
export const notifyStatusChange = async (quotation, oldStatus, newStatus) => {
  try {
    // Si los estados son iguales, no enviamos notificación
    if (oldStatus === newStatus) {
      return null;
    }

    // Determinar el email del destinatario (usuario o email de cotización)
    let userEmail;
    let userName = 'Cliente';
    const user = quotation.get('user');
    
    if (user) {
      // Si la cotización está asociada a un usuario, obtenemos su email
      try {
        await user.fetch();
        userEmail = user.get('email');
        userName = user.get('name') || user.get('username') || 'Cliente';
      } catch (error) {
        console.warn('No se pudo obtener el email del usuario:', error);
      }
    }
    
    // Si no hay usuario o no se pudo obtener su email, usamos el email de la cotización
    if (!userEmail) {
      userEmail = quotation.get('userEmail');
      userName = quotation.get('userName') || 'Cliente';
    }

    if (!userEmail) {
      console.warn('No se puede enviar notificación: email de usuario no disponible');
      return null;
    }

    // Si el nuevo estado es 'estimado_listo', usamos la plantilla específica
    if (newStatus === 'estimado_listo') {
      const quotationId = quotation.id;
      
      // Generar URLs para seguimiento y detalle (magic link)
      const magicLink = quotation.get('magicLink');
      const appUrl = getAppUrl();
      const trackUrl = `${appUrl}/quotation/track/${magicLink}`;
      const detailUrl = `${appUrl}/quotation/detail/${magicLink}`;
      const authUrl = `${appUrl}/quotation/auth/${magicLink}`; // URL para crear cuenta
      
      console.log('Generando URLs para cotización lista:', {
        quotationId,
        magicLink,
        appUrl,
        trackUrl,
        detailUrl,
        authUrl
      });
      
      // Usar la plantilla de estimado listo
      const result = await sendTemplateEmail({
        to: userEmail,
        templateId: TEMPLATE_IDS.QUOTATION_READY,
        dynamicTemplateData: {
          nombre_cliente: userName,
          id_cotizacion: quotationId,
          enlace_propuesta: detailUrl,     // Enlace para ver detalles completos
          enlace_seguimiento: trackUrl,    // Enlace para seguimiento de estado
          enlace_cuenta: authUrl,          // Enlace para crear cuenta
          enlace_magic: magicLink,         // Magic link sin la URL base
          app_url: appUrl                  // URL base de la aplicación
        }
      });
      
      if (result) {
        console.log(`Email de estimado listo enviado exitosamente a ${userEmail} usando plantilla ${TEMPLATE_IDS.QUOTATION_READY}`);
      } else {
        console.warn(`No se pudo enviar el email de estimado listo a ${userEmail} usando plantilla. Intentando método alternativo...`);
        // Si falla la plantilla, intentamos con el método tradicional
        return await sendStatusUpdateNotification(quotation, userEmail, oldStatus, newStatus);
      }
      
      return result;
    }
    
    // Si el nuevo estado es 'rechazada', enviamos una notificación específica
    if (newStatus === 'rechazada') {
      const quotationId = quotation.id;
      
      // Enviamos un email personalizado para cotizaciones rechazadas
      return await sendEmail({
        to: userEmail,
        subject: 'Actualización sobre su cotización en VV Health',
        text: `Estimado/a ${userName},\n\nLamentamos informarle que no podemos proceder con su cotización #${quotationId} en este momento. Esto puede deberse a que los productos solicitados no están disponibles o no podemos satisfacer sus requerimientos específicos.\n\nSi desea obtener más información o explorar alternativas, no dude en contactarnos directamente.\n\nGracias por su interés en VV Health.\n\nAtentamente,\nEl equipo de VV Health`,
        html: `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
            <h2 style="color: #2a5885;">Actualización sobre su cotización</h2>
            <p>Estimado/a ${userName},</p>
            <p>Lamentamos informarle que no podemos proceder con su cotización <strong>#${quotationId}</strong> en este momento. Esto puede deberse a que los productos solicitados no están disponibles o no podemos satisfacer sus requerimientos específicos.</p>
            <p>Si desea obtener más información o explorar alternativas, no dude en contactarnos directamente.</p>
            <p>Gracias por su interés en VV Health.</p>
            <p>Atentamente,<br>El equipo de VV Health</p>
          </div>
        `
      });
    }

    // Para otros estados, usamos el método anterior
    return await sendStatusUpdateNotification(quotation, userEmail, oldStatus, newStatus);
  } catch (error) {
    console.error('Error al enviar notificación de cambio de estado:', error);
    // No lanzamos el error para evitar interrumpir el flujo principal
    return null;
  }
};

/**
 * Envía un email de bienvenida cuando un usuario crea una cuenta
 * @param {Object} user - Objeto de usuario de Parse
 * @returns {Promise} - Promesa que se resuelve cuando el email es enviado
 */
export const notifyNewUser = async (user) => {
  try {
    return await sendWelcomeEmail(user);
  } catch (error) {
    console.error('Error al enviar email de bienvenida:', error);
    // No lanzamos el error para evitar interrumpir el flujo principal
    return null;
  }
};

/**
 * Actualiza el estado de una cotización y envía una notificación
 * @param {Object} quotation - Objeto de cotización de Parse
 * @param {string} newStatus - Nuevo estado de la cotización
 * @param {string} [userId] - ID del usuario que realiza el cambio (opcional)
 * @returns {Promise} - Promesa que se resuelve cuando la cotización es actualizada y la notificación enviada
 */
export const updateQuotationStatusWithNotification = async (quotation, newStatus, userId = null) => {
  try {
    // Guardar el estado actual antes de actualizarlo
    const oldStatus = quotation.get('status') || 'pending';
    
    // Si el estado no ha cambiado, no hacemos nada
    if (oldStatus === newStatus) {
      console.log(`El estado de la cotización ${quotation.id} ya es '${newStatus}'. No se realizará ningún cambio.`);
      return quotation;
    }
    
    console.log(`Actualizando estado de cotización ${quotation.id} de '${oldStatus}' a '${newStatus}'`);
    
    // Actualizar el estado
    quotation.set('status', newStatus);
    
    // Guardar el historial de cambios de estado
    const statusHistory = quotation.get('statusHistory') || [];
    statusHistory.push({
      status: newStatus,
      date: new Date().toISOString(),
      by: userId || 'system' // Usar el ID del usuario si está disponible, o 'system' por defecto
    });
    quotation.set('statusHistory', statusHistory);
    
    // Guardar la cotización
    await quotation.save(null, { useMasterKey: true });
    console.log(`Cotización ${quotation.id} actualizada con éxito al estado '${newStatus}'`);
    
    // Enviar notificación
    const notificationResult = await notifyStatusChange(quotation, oldStatus, newStatus);
    if (notificationResult) {
      console.log(`Notificación de cambio de estado enviada para cotización ${quotation.id}`);
    } else {
      console.warn(`No se pudo enviar la notificación de cambio de estado para cotización ${quotation.id}`);
    }
    
    return quotation;
  } catch (error) {
    console.error(`Error al actualizar estado de cotización ${quotation.id} a '${newStatus}':`, error);
    throw new Error(`Error al actualizar estado: ${error.message}`);
  }
};

/**
 * Envía un email al equipo de ventas cuando un usuario solicita información adicional
 * @param {Object} quotation - Objeto de cotización de Parse
 * @param {Object} user - Objeto de usuario de Parse
 * @returns {Promise} - Promesa que se resuelve cuando el email es enviado
 */
export const contactSalesTeam = async (quotation, user) => {
  try {
    if (!quotation) {
      console.warn('No se puede enviar consulta a ventas: cotización no disponible');
      return null;
    }

    // Obtener datos para la plantilla
    const userEmail = user ? user.get('email') : quotation.get('userEmail');
    
    // Obtener y formatear el nombre del cliente
    let rawName = user ? user.get('name') || user.get('firstName') : quotation.get('userName') || '';
    let userName = rawName;
    
    // Ensure userName is not empty and properly formatted
    if (!userName || userName.trim() === '') {
      userName = 'Cliente';
    } else {
      // Capitalize first letter of each word
      userName = userName.split(' ').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      ).join(' ');
    }
    
    console.log('Sending sales team email with client name:', userName);
    
    const quotationId = quotation.id;
    const productType = quotation.get('productType') || 'Producto';
    const status = quotation.get('status') || 'pending';
    
    // Usar la plantilla de bienvenida adaptada para consulta de ventas
    const result = await sendTemplateEmail({
      to: ['info@vvhealthpr.com'], // Email oficial para notificaciones
      templateId: TEMPLATE_IDS.WELCOME_QUOTATION,
      dynamicTemplateData: {
        nombre_cliente: userName,
        email_cliente: userEmail,
        tipo_producto: productType,
        id_cotizacion: quotationId,
        asunto: `Consulta sobre cotización #${quotationId}`,
        mensaje_adicional: `El cliente ha solicitado información adicional sobre su cotización #${quotationId} que actualmente está en estado: ${status}.`,
        app_url: getAppUrl()
      }
    });

    if (result) {
      console.log(`Email de consulta a ventas enviado exitosamente para la cotización ${quotationId}`);
    } else {
      console.warn(`No se pudo enviar el email de consulta a ventas para la cotización ${quotationId}`);
    }
    
    return result;
  } catch (error) {
    console.error('Error al enviar consulta a ventas:', error);
    // No lanzamos el error para evitar interrumpir el flujo principal
    return null;
  }
};

/**
 * Envía un email de cotización actualizada al cliente usando la plantilla 'Estimado Listo'.
 * @param {Object} params - Parámetros: quotationId, items, notes
 * @returns {Promise}
 */
export const sendUpdatedQuotationEmail = async ({ quotationId, items, notes }) => {
  try {
    // Obtener la cotización y datos del cliente
    const Parse = (await import('../../config/parse')).default;
    const Quotation = Parse.Object.extend('Quotation');
    const query = new Parse.Query(Quotation);
    const quotation = await query.get(quotationId);
    const userEmail = quotation.get('userEmail');
    
    // Obtener y formatear el nombre del cliente
    let userName = quotation.get('userName') || '';
    // Ensure userName is not empty and properly formatted
    if (!userName || userName.trim() === '') {
      userName = 'Cliente';
    } else {
      // Capitalize first letter of each word
      userName = userName.split(' ').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      ).join(' ');
    }
    
    console.log('Sending updated quotation email with client name:', userName);
    
    const magicLink = quotation.get('magicLink');
    const appUrl = import.meta.env.VITE_APP_URL || 'http://localhost:5173';
    const proposalUrl = `${appUrl}/quotation/detail/${magicLink}`;

    // Enviar email usando plantilla de "Estimado Listo"
    const { sendTemplateEmail, TEMPLATE_IDS } = await import('./sendgridService');
    return sendTemplateEmail({
      to: userEmail,
      templateId: TEMPLATE_IDS.QUOTATION_READY,
      dynamicTemplateData: {
        nombre_cliente: userName,
        id_cotizacion: quotationId,
        enlace_propuesta: proposalUrl
        // Puedes añadir más variables dinámicas aquí si la plantilla lo requiere
      }
    });
  } catch (error) {
    console.error('Error al enviar email de cotización actualizada:', error);
    throw new Error('No se pudo enviar el email de cotización actualizada');
  }
};

/**
 * Función para enviar un email de prueba a un admin para verificar el formato
 * @param {string} testEmail - Email de prueba para recibir la notificación
 * @returns {Promise<boolean>} - true si se envió correctamente
 */
export const sendTestAdminEmail = async (testEmail) => {
  try {
    console.log(`Enviando email de prueba a ${testEmail}...`);
    
    // Datos de prueba
    const sampleData = {
      quotationId: 'TEST-' + Math.floor(Math.random() * 100000),
      userName: 'Cliente Prueba',
      userEmail: 'cliente@ejemplo.com',
      userPhone: '787-123-4567',
      userCompany: 'Empresa de Prueba',
      message: 'Este es un mensaje de prueba para ver si se recibe correctamente.',
      additionalNotes: 'Notas adicionales de prueba para la cotización.',
      items: [
        {
          name: 'Vitamina C 1000mg',
          code: 'VIT-C-1000',
          countPerBottle: ['60ct'],
          bulkQuantity: 5
        },
        {
          name: 'Complejo B Complex',
          code: 'B-COMPLEX-100',
          countPerBottle: ['100ct'],
          bulkQuantity: 3
        },
        {
          name: 'Omega 3 Fish Oil',
          code: 'OMEGA3-1000',
          countPerBottle: ['90ct'],
          bulkQuantity: 2
        }
      ]
    };
    
    // Variables de la cotización de prueba
    const { 
      quotationId, 
      userName, 
      userEmail, 
      userPhone, 
      userCompany, 
      message, 
      additionalNotes, 
      items 
    } = sampleData;
    
    // Crear la lista de productos
    const itemsText = items.map(item => `- ${item.name}: ${item.bulkQuantity} unidades`).join('\n');
    const productList = items.map(item => `<tr>
      <td>${item.name || 'No especificado'}</td>
      <td>${item.code || 'Sin SKU'}</td>
      <td>${(item.countPerBottle && item.countPerBottle[0]) ? item.countPerBottle[0] : 'N/A'}</td>
      <td align="right">${item.bulkQuantity}</td>
    </tr>`).join('');
    
    // URL administrativa
    const appUrl = import.meta.env.VITE_APP_URL || 'http://localhost:5173';
    const adminUrl = `${appUrl}/admin/quotations/${quotationId}`;
    
    // HTML personalizado para el administrador
    const adminHtmlContent = `
    <!DOCTYPE html>
    <html lang="es">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Nueva Solicitud de Cotización - VV Health</title>
      <style>
        body {
          font-family: Arial, sans-serif;
          line-height: 1.6;
          color: #333;
          margin: 0;
          padding: 0;
        }
        .container {
          max-width: 600px;
          margin: 0 auto;
          background-color: #ffffff;
          border: 1px solid #e0e0e0;
          border-radius: 5px;
        }
        .header {
          background-color: #3c8653;
          color: white;
          padding: 20px;
          text-align: center;
        }
        .content {
          padding: 30px;
        }
        .notification-banner {
          background-color: #f8f1d5;
          border-left: 4px solid #e6bb46;
          padding: 12px 20px;
          margin-bottom: 20px;
          font-weight: bold;
        }
        .client-info {
          background-color: #f5f5f5;
          border-radius: 5px;
          padding: 15px;
          margin-bottom: 20px;
        }
        .action-button {
          display: inline-block;
          background-color: #3c8653;
          color: white;
          text-decoration: none;
          padding: 12px 20px;
          border-radius: 4px;
          margin-top: 10px;
        }
        .footer {
          background-color: #f5f5f5;
          padding: 20px;
          text-align: center;
          font-size: 12px;
          color: #666;
        }
        table {
          width: 100%;
          border-collapse: collapse;
        }
        table, th, td {
          border: 1px solid #ddd;
        }
        th, td {
          padding: 8px;
        }
        th {
          background-color: #f2f2f2;
          text-align: left;
        }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="header">
          <h2>NOTIFICACIÓN DE NUEVA COTIZACIÓN (PRUEBA)</h2>
        </div>
        <div class="content">
          <div class="notification-banner">
            Se ha recibido una nueva solicitud de cotización que requiere revisión inmediata.
          </div>
          
          <p>Estimado Equipo de VV Health,</p>
          
          <p>El cliente <strong>${userName}</strong> ha enviado una nueva solicitud de cotización a través del sitio web. A continuación, los detalles:</p>
          
          <div class="client-info">
            <h3>Información del Cliente</h3>
            <p><strong>Nombre:</strong> ${userName}</p>
            <p><strong>Email:</strong> ${userEmail}</p>
            <p><strong>Teléfono:</strong> ${userPhone}</p>
            <p><strong>Empresa:</strong> ${userCompany}</p>
            <p><strong>ID de Cotización:</strong> #${quotationId}</p>
            <p><strong>Fecha de Solicitud:</strong> ${new Date().toLocaleString('es-PR')}</p>
          </div>
          
          <h3>Productos Solicitados (${items.length})</h3>
          <table>
            <thead>
              <tr>
                <th>Producto</th>
                <th>SKU</th>
                <th>Pastillas/Frasco</th>
                <th align="right">Cantidad</th>
              </tr>
            </thead>
            <tbody>
              ${productList}
            </tbody>
          </table>
          
          <div class="client-info" style="margin-top: 20px;">
            <h3>Mensaje del Cliente</h3>
            <p>${message || 'No se proporcionó mensaje.'}</p>
            
            <h3>Notas Adicionales</h3>
            <p>${additionalNotes || 'No se proporcionaron notas adicionales.'}</p>
          </div>
          
          <p>Por favor, proceda a revisar esta solicitud y preparar una cotización para el cliente lo antes posible.</p>
          
          <a href="${adminUrl}" class="action-button">Revisar en el Panel de Administración</a>
          
          <p style="margin-top: 30px;">Gracias por su atención.</p>
          <p>Notificación Automática - Sistema de Cotizaciones VV Health</p>
        </div>
        
        <div class="footer">
          <p>&copy; ${new Date().getFullYear()} VV Health. Todos los derechos reservados.</p>
          <p>Esta es una notificación automática DE PRUEBA. Por favor no responda a este correo.</p>
        </div>
      </div>
    </body>
    </html>
    `;
    
    // Importamos el servicio de email
    const { sendEmail } = await import('./sendgridService');
    
    // Enviamos como HTML regular
    const result = await sendEmail({
      to: testEmail,
      subject: `[PRUEBA] Nueva Cotización - ${userName} - #${quotationId}`,
      html: adminHtmlContent,
      text: `PRUEBA - NUEVA SOLICITUD DE COTIZACIÓN\n\nCliente: ${userName}\nEmail: ${userEmail}\nTeléfono: ${userPhone}\nEmpresa: ${userCompany}\n\nProductos (${items.length}):\n${itemsText}\n\nMensaje: ${message || 'No proporcionado'}\n\nNotas: ${additionalNotes || 'No proporcionadas'}\n\nRevise esta solicitud en: ${adminUrl}\n\nGracias, Sistema de Cotizaciones VV Health`
    });
    
    if (result) {
      console.log(`✅ Email de prueba enviado exitosamente a ${testEmail}`);
      return true;
    } else {
      console.error(`❌ No se pudo enviar el email de prueba a ${testEmail}`);
      return false;
    }
  } catch (error) {
    console.error('❌ Error al enviar email de prueba:', error);
    return false;
  }
};

export default {
  notifyNewQuotation,
  notifyStatusChange,
  sendQuotationConfirmation,
  updateQuotationStatusWithNotification,
  contactSalesTeam,
  sendUpdatedQuotationEmail,
  sendTestAdminEmail
};
